/* global TimeoutId */
import { Component, ReactNode } from 'react';

// Use a one second interval.
const INTERVAL_DURATION = 1000;

// The checks should come more frequently, since setInterval is not guaranteed to
// occur in a timely manner. Have duration overlap with the interval to make tests
// easier to write.
const TICK_DURATION = 250;

type ResetKey = string | number;

interface IProps {
  children: (remaining: number) => ReactNode;
  onFinish?: () => any;
  milliseconds: number;
  resetKey?: ResetKey;
}

interface IState {
  remaining: number;
  resetKey?: ResetKey;
  startTime: number;
  totalIntervals: number;
}

const getInitialState = (milliseconds: number, intervalDuration: number, resetKey?: ResetKey): IState => {
  const totalIntervals = Math.floor(milliseconds / intervalDuration);
  return { remaining: totalIntervals, resetKey, startTime: Date.now(), totalIntervals };
};

/**
 * Utility method for maintaining a countdown and rendering relevant information
 * about the countdown.
 *
 * If changing the timeout is required, then this component should be called by using
 * the `resetKey`. This will not cause reconciliation, but rather restart the timeout.
 *
 * If reconciliation is desireable, then provide the `key` property.
 */
class Countdown extends Component<IProps, IState> {
  public static getDerivedStateFromProps(nextProps: IProps, prevState: IState) {
    // If the reset key has changed, update the state to reflect that.
    if (nextProps.resetKey !== prevState.resetKey) {
      return getInitialState(nextProps.milliseconds, INTERVAL_DURATION, nextProps.resetKey);
    }

    return null;
  }

  private timeoutId?: TimeoutId;

  public constructor(props: IProps) {
    super(props);
    this.state = getInitialState(props.milliseconds, INTERVAL_DURATION, props.resetKey);
  }

  public render() {
    return this.props.children((this.state.remaining));
  }

  public componentDidMount() {
    this.init();
  }

  public componentDidUpdate(prevProps: IProps, prevState: IState) {
    // The reset key has changed, simulate unmounting and mounting.
    if (prevState.resetKey !== this.state.resetKey) {
      this.componentWillUnmount();
      this.componentDidMount();
    }
  }

  public componentWillUnmount() {
    this.stop();
  }

  private init() {
    const duration = this.state.remaining > 0 ? TICK_DURATION : 0;
    this.timeoutId = setInterval(this.tick, duration);
  }

  private tick = () => {
    this.setState((prevState) => {
      const elapsed = Math.floor((Date.now() - prevState.startTime) / INTERVAL_DURATION);
      const remaining = Math.max(0, prevState.totalIntervals - elapsed);

      // The countdown has run out, set it to 0 and call onFinish.
      if (remaining === 0) {
        this.stop();

        if (this.props.onFinish) {
          this.props.onFinish();
        }
      }

      if (remaining !== prevState.remaining) {
        return { remaining };
      }

      return null;
    });
  };

  private stop() {
    if (this.timeoutId) {
      clearInterval(this.timeoutId);
    }
  }
}

export default Countdown;
